import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Subscription, forkJoin } from 'rxjs';

import { LoaderComponent } from 'src/app/components/widgets/loader/loader.component';
import { DataListComponent } from 'src/app/components/widgets/data-list/data-list.component';
import DataListConfig from 'src/app/components/widgets/data-list/data-list.config';
import { RegulatorsService } from 'src/app/services/api/regulators.service';
import { AccessControlService } from 'src/app/services/access-control.service';
import { CommonService } from 'src/app/services/common.service';
import { MainLayoutService } from 'src/app/services/main-layout.service';
import { RouteService } from 'src/app/services/route.service';
import {
  Regulator,
  Vlt,
  regulatorStatusBadgeClass,
  vltHasCoordinates,
  vltStatusLabel
} from 'src/app/models/entities/regulator';
import { RegulatorContextBoxComponent, RegulatorContextItem } from '../components/regulator-context-box/regulator-context-box.component';

const OPEN_STREET_MAP_URL = 'https://www.openstreetmap.org';

const MAP_LATITUDE_SPAN = 0.004;
const MAP_LONGITUDE_SPAN = 0.006;

@Component({
  selector: 'app-regulator-vlt-dashboard-page',
  imports: [LoaderComponent, DataListComponent, RegulatorContextBoxComponent],
  templateUrl: './regulator-vlt-dashboard-page.component.html',
  styleUrl: './regulator-vlt-dashboard-page.component.scss',
})
export class RegulatorVltDashboardPageComponent implements OnInit, OnDestroy {

  readonly regulatorId: number;
  readonly vltId: number;
  readonly statusLabel = vltStatusLabel;
  readonly statusBadgeClass = regulatorStatusBadgeClass;

  isLoading = true;
  canModify = false;
  vlt: Vlt | null = null;
  contextItems: RegulatorContextItem[] = [];
  mapEmbedUrl: SafeResourceUrl | null = null;
  mapExternalUrl: string | null = null;

  detailsConfig: DataListConfig = {
    data: null,
    rows: [
      { attribute: 'code', label: 'Code', value: (vlt: Vlt) => vlt?.code ?? '' },
      { attribute: 'serialNumber', label: 'Serial Number', value: (vlt: Vlt) => vlt?.serialNumber ?? '' },
      { attribute: 'manufacturer', label: 'Manufacturer', value: (vlt: Vlt) => vlt?.manufacturer ?? '' },
      { attribute: 'model', label: 'Model', value: (vlt: Vlt) => vlt?.model ?? '' },
      { attribute: 'venue', label: 'Venue', value: (vlt: Vlt) => vlt?.venue ?? '' },
      { attribute: 'address', label: 'Address', value: (vlt: Vlt) => vlt?.address ?? '' },
      { attribute: 'city', label: 'City', value: (vlt: Vlt) => vlt?.city ?? '' },
      { attribute: 'country', label: 'Country', value: (vlt: Vlt) => vlt?.country ?? '' },
      { attribute: 'coordinates', label: 'Coordinates', value: (vlt: Vlt) => this.formatCoordinates(vlt) },
    ]
  };

  constructor(
    private regulatorsService: RegulatorsService,
    private routeService: RouteService,
    private mainLayoutService: MainLayoutService,
    private accessControlService: AccessControlService,
    private commonService: CommonService,
    private sanitizer: DomSanitizer,
    private route: ActivatedRoute,
    private router: Router
  ) {
    this.regulatorId = Number(this.route.snapshot.paramMap.get('regulatorId'));
    this.vltId = Number(this.route.snapshot.paramMap.get('vltId'));
  }

  private subscriptions = new Subscription();

  ngOnInit(): void {
    this.subscriptions.add(
      this.mainLayoutService.activeProfile$.subscribe(profileCode => {
        this.canModify = this.accessControlService.hasFeatureActionAccess(profileCode, 'REGULATORS_LIST', 'modify');
      })
    );

    forkJoin({
      regulator: this.regulatorsService.detail(this.regulatorId),
      vlt: this.regulatorsService.detailVlt(this.regulatorId, this.vltId),
    }).subscribe({
      next: ({ regulator, vlt }: any) => {
        this.applyVlt(regulator?.data?.regulator, vlt?.data?.vlt);
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
        this.router.navigate(['/error/404']);
      }
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.mainLayoutService.setBreadcrumbs(null);
  }

  editVlt(): void {
    this.routeService.regulatorVltUpdate(this.regulatorId, this.vltId);
  }

  goBack(): void {
    this.routeService.regulatorVlts(this.regulatorId);
  }

  formatDateTime(value: string | null | undefined): string {
    return this.commonService.formatDateValue(value, true);
  }

  private applyVlt(regulator: Regulator | null | undefined, vlt: Vlt | null | undefined): void {
    if (!regulator || !vlt) {
      return;
    }

    this.vlt = vlt;
    this.detailsConfig = { ...this.detailsConfig, data: vlt };
    this.contextItems = [
      { label: 'Regulator', code: regulator.code, value: regulator.name },
      { label: 'VLT', code: vlt.code },
    ];
    this.buildMapUrls(vlt);
    this.mainLayoutService.setBreadcrumbs([
      { label: 'Regulators', routerLink: '/regulators' },
      { label: regulator.code },
      { label: 'VLT', routerLink: `/regulators/${regulator.regulatorId}/vlt` },
      { label: vlt.code },
    ]);
  }

  private buildMapUrls(vlt: Vlt): void {
    if (!vltHasCoordinates(vlt)) {
      this.mapEmbedUrl = null;
      this.mapExternalUrl = null;
      return;
    }

    const latitude = Number(vlt.latitude);
    const longitude = Number(vlt.longitude);
    const boundingBox = [
      longitude - MAP_LONGITUDE_SPAN,
      latitude - MAP_LATITUDE_SPAN,
      longitude + MAP_LONGITUDE_SPAN,
      latitude + MAP_LATITUDE_SPAN,
    ].join(',');

    this.mapEmbedUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
      `${OPEN_STREET_MAP_URL}/export/embed.html?bbox=${boundingBox}&layer=mapnik&marker=${latitude},${longitude}`
    );
    this.mapExternalUrl = `${OPEN_STREET_MAP_URL}/?mlat=${latitude}&mlon=${longitude}#map=17/${latitude}/${longitude}`;
  }

  private formatCoordinates(vlt: Vlt): string {
    if (!vltHasCoordinates(vlt)) {
      return '';
    }
    return `${Number(vlt.latitude).toFixed(6)}, ${Number(vlt.longitude).toFixed(6)}`;
  }
}
